import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import DataRoomMemberInfo from 'router/subrouters/DataRoom/components/DataRoomMemberInfo';
import QaRolesCustomSelect from 'components/QaRolesCustomSelect';
import { Button, Loader } from 'ncoded-component-library';
import TrashIcon from 'icons/Trash.icon';
import { useTranslation } from 'react-i18next';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import MemberRoles from 'router/subrouters/DataRoom/components/MemberRoles';
import confirm from 'modules/confirm';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './ManageTeamMemberDrawer.styles.scss';
import './ManageTeamMemberDrawer.styles.responsive.scss';

type ManageTeamMemberDrawerProps = {
  className?: string;
  member: DataRoomMember;
  updateMember?: (member: DataRoomMember) => void;
  updateDataRoomMembers?: (members: DataRoomMembers) => void;
};

const ManageTeamMemberDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageTeamMemberDrawerProps
> = (props, ref) => {
  const { className, member, updateMember, updateDataRoomMembers } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { buyerQaRole } = useOptions();

  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('manage-team-member-drawer', className);

  const { t } = useTranslation();

  const handleRemoveMember = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('removeMember'),
          content: t('removeMemberMessage'),
        },
        true,
      )
    ) {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.removeMember(member?._id);
        updateDataRoomMembers?.(data);
        drawer?.setOpen(false);
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [member, drawer, updateDataRoomMembers, popServerError, t]);

  const onSubmit = useCallback(
    async (values: Partial<DataRoomMember>) => {
      try {
        setIsLoading(true);

        await dataRoomApi.members.updateMember(member?._id, values);

        updateMember?.({ ...member, ...values });
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [member, popServerError, updateMember],
  );

  const DeleteButton = useMemo(
    () => (
      <Button
        variant="link"
        icon={TrashIcon}
        disabled={isLoading}
        className="warning-action-button"
        onClick={handleRemoveMember}
      >
        {t('deleteMember')}
      </Button>
    ),
    [isLoading, handleRemoveMember, t],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes}>
      <DataRoomMemberInfo className="white-panel" member={member} isBuySide />

      {member?.requestedDataRoomAccess ? (
        <MemberRoles defaultOpen>
          <QaRolesCustomSelect
            name="qaRole"
            label={t('qaRole')}
            options={buyerQaRole}
            renderAsPortal
            value={member?.qaRole}
            onChange={onSubmit}
          />

          {DeleteButton}

          {isLoading && <Loader />}
        </MemberRoles>
      ) : (
        DeleteButton
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageTeamMemberDrawer);
